import { Controller } from '@hotwired/stimulus'

// Publishes the report row's distance from the top of the document as
// `--report-row-top`, so the legacy layout's stylesheet can size the row to
// the viewport from where the row really is. The legacy layout has no
// full-height flex chain: it estimates the space above the row from the
// chrome heights, and a banner or badge that changes the offset without
// changing those heights leaves the row short or overflowing.
export default class extends Controller<HTMLElement> {
  private observer: ResizeObserver | null = null

  private frame: number | null = null

  connect(): void {
    this.update()
    window.addEventListener('resize', this.scheduleUpdate)

    if (typeof ResizeObserver === 'undefined') return

    this.observer = new ResizeObserver(this.scheduleUpdate)
    // Content above the row that grows moves the row down and the page grows
    // with it. Content that shrinks (a flash the table frame clears) does not
    // shrink the page below the viewport, so the body alone misses it: the
    // chrome wrappers above the row are observed as well.
    this.observer.observe(document.body)
    this.chromeWrappersAboveRow().forEach((wrapper) => {
      this.observer?.observe(wrapper)
    })
  }

  disconnect(): void {
    window.removeEventListener('resize', this.scheduleUpdate)
    this.observer?.disconnect()
    this.observer = null
    if (this.frame !== null) window.cancelAnimationFrame(this.frame)
    this.frame = null
    this.element.style.removeProperty('--report-row-top')
  }

  // The layout wraps each piece of chrome whose height it tracks in an
  // `sf-ce-height-watcher`. The watcher itself is inline and never reports a
  // size change, so its block parent is what gets observed.
  private chromeWrappersAboveRow(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>('sf-ce-height-watcher')
    )
      .filter(
        (watcher) =>
          watcher.compareDocumentPosition(this.element) &
          Node.DOCUMENT_POSITION_FOLLOWING
      )
      .map((watcher) => watcher.parentElement)
      .filter((wrapper): wrapper is HTMLElement => wrapper !== null)
  }

  // One measurement per frame: `resize` fires many times per second while the
  // window is dragged, each measurement forces a layout, and a measurement
  // inside a ResizeObserver callback would resize the observed body again.
  private scheduleUpdate = (): void => {
    if (this.frame !== null) return
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  private update = (): void => {
    const top = this.element.getBoundingClientRect().top + window.scrollY
    this.element.style.setProperty('--report-row-top', `${Math.round(top)}px`)
  }
}
